/**
 * Centralized Rules Engine for German License Plates
 * 
 * This engine is completely data-driven - no hardcoded logic in UI components.
 * All validation is computed dynamically from the rules database.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type VehicleCategory =
  | 'PKW'           // Personenkraftwagen (Car)
  | 'MOTORCYCLE'    // Motorrad
  | 'TRUCK'         // Lastkraftwagen (Truck)
  | 'TRAILER'       // Anhänger
  | 'LEICHTKRAFT'   // Leichtkraftrad (125cc)
  | 'TRAKTOR'       // Traktor (Tractor)

export type PlateLayout = 'einzeilig' | 'zweizeilig'

export type PlateType =
  | 'REGULAR'
  | 'REGULAR_SEASON'
  | 'E'
  | 'E_SEASON'
  | 'H'
  | 'H_SEASON'
  | 'GREEN'
  | 'SHORT_TERM'
  | 'EXPORT'

export interface PlateSize {
  width: number  // mm
  height: number // mm
  layout: PlateLayout
}

export interface LetterNumberCombination {
  letters: number  // Number of letters after city code (1-2)
  numbers: number  // Number of numbers after letters (1-4)
}

/**
 * City-code-dependent combinations
 * Some rules depend on the city code length (1, 2, or 3 letters)
 */
export interface CityCodeDependentCombinations {
  cityCodeLength1?: LetterNumberCombination[]  // Rules when city code = 1 letter
  cityCodeLength2?: LetterNumberCombination[]  // Rules when city code = 2 letters
  cityCodeLength3?: LetterNumberCombination[]  // Rules when city code = 3 letters
}

export interface PlateRule {
  // Identification
  vehicleCategory: VehicleCategory
  plateSize: PlateSize
  plateType: PlateType

  // Limits
  maxTotalChars: number
  maxCityCodeLength: number
  minCityCodeLength: number

  // Allowed combinations - can be simple or city-code-dependent
  allowedCombinations?: LetterNumberCombination[]  // Simple: same for all city code lengths
  cityCodeDependentCombinations?: CityCodeDependentCombinations  // Complex: depends on city code length

  // Options
  allowsTwoLine: boolean
  seasonalMarkersCount: boolean // Whether E/H markers count toward limit (they NEVER reduce it)

  // Metadata
  description?: string
}

// ============================================================================
// RULES DATABASE
// ============================================================================

/**
 * Central rules database - all plate configurations are defined here.
 * Add new rules by adding objects to this array.
 */
// ============================================================================
// PKW & LKW & TRAILER - Standard Sizes (EXACT RULES)
// ============================================================================

/**
 * Central rules database - all plate configurations are defined here.
 * Add new rules by adding objects to this array.
 */
const PLATE_RULES: PlateRule[] = [

  // PKW/LKW/TRAILER - 520 × 110 mm
  // Max: 8 characters, ALL combinations allowed
  ...(['PKW', 'TRUCK', 'TRAILER'] as VehicleCategory[]).map(cat => ({
    vehicleCategory: cat,
    plateSize: { width: 520, height: 110, layout: 'einzeilig' as PlateLayout },
    plateType: 'REGULAR' as PlateType,
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 }, { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Standard 520mm - Max 8 chars',
  })),

  // PKW/LKW/TRAILER - 480 × 110 mm & 460 × 110 mm
  // Max: 6 characters
  // If city code = 3 letters: 2 letters + 1 digit OR 1 letter + 2 digits
  ...(['PKW', 'TRUCK', 'TRAILER'] as VehicleCategory[]).flatMap(cat =>
    [{ w: 480, h: 110 }, { w: 460, h: 110 }].map(size => ({
      vehicleCategory: cat,
      plateSize: { width: size.w, height: size.h, layout: 'einzeilig' as PlateLayout },
      plateType: 'REGULAR' as PlateType,
      maxTotalChars: 6,
      maxCityCodeLength: 3,
      minCityCodeLength: 1,
      cityCodeDependentCombinations: {
        cityCodeLength1: [
          { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
          { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 },
        ],
        cityCodeLength2: [
          { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 },
          { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 },
        ],
        cityCodeLength3: [
          { letters: 2, numbers: 1 },  // Simplified limit: 2 letters -> 1 digit
          { letters: 1, numbers: 2 },  // Simplified limit: 1 letter -> 2 digits
        ],
      },
      allowsTwoLine: false,
      seasonalMarkersCount: false,
      description: `${size.w}mm - Max 6 chars, strict 3-letter city rules`,
    }))
  ),

  // PKW/LKW/TRAILER - 420 × 110 mm
  // Max: 5 characters
  // City code 3 letters -> ONLY 1 letter + 1 digit
  ...(['PKW', 'TRUCK', 'TRAILER'] as VehicleCategory[]).map(cat => ({
    vehicleCategory: cat,
    plateSize: { width: 420, height: 110, layout: 'einzeilig' as PlateLayout },
    plateType: 'REGULAR' as PlateType,
    maxTotalChars: 5,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 1, numbers: 1 },  // STRICT: Only 1 letter + 1 number
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '420mm - Max 5 chars, strict 3-letter city rules',
  })),

  // PKW/LKW/TRAILER - 400 × 110 mm
  // Max: 4 characters
  // City code max 2 letters
  // Includes special short formats: City(2)+Num(2), City(1)+Num(3)
  ...(['PKW', 'TRUCK', 'TRAILER'] as VehicleCategory[]).map(cat => ({
    vehicleCategory: cat,
    plateSize: { width: 400, height: 110, layout: 'einzeilig' as PlateLayout },
    plateType: 'REGULAR' as PlateType,
    maxTotalChars: 4,
    maxCityCodeLength: 2,  // STRICT: No 3-letter city codes
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
        { letters: 0, numbers: 3 }, // Allowed Example: City(1) + Numbers(3)
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 2, numbers: 1 }, // Note: 2+2+1=5 (Blocked by maxTotalChars=4)
        { letters: 0, numbers: 2 }, // Allowed Example: City(2) + Numbers(2)
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '400mm - Max 4 chars, no 3-letter city codes',
  })),

  // ============================================================================
  // Two-line Plates (280-340 × 200 mm)
  // Max: 8 characters
  // ============================================================================

  ...(['PKW', 'TRUCK', 'TRAILER'] as VehicleCategory[]).flatMap(cat =>
    [{ w: 340, h: 200 }, { w: 320, h: 200 }, { w: 280, h: 200 }].map(size => ({
      vehicleCategory: cat,
      plateSize: { width: size.w, height: size.h, layout: 'zweizeilig' as PlateLayout },
      plateType: 'REGULAR' as PlateType,
      maxTotalChars: 8,
      maxCityCodeLength: 3,
      minCityCodeLength: 1,
      allowedCombinations: [
        { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
        { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 }, { letters: 2, numbers: 4 },
      ],
      allowsTwoLine: true,
      seasonalMarkersCount: false,
      description: `Two-line ${size.w}mm - Max 8 chars`,
    }))
  ),

  // ============================================================================
  // MOTORCYCLE PLATES
  // ============================================================================

  // Motorcycle - 220 × 200 mm
  // Max: 8 characters
  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 220, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 }, { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Motorcycle 220x200mm - Max 8 chars',
  },

  // Motorcycle - 200 × 200 mm & 180 x 200 mm
  // Max: 7 characters
  ...[{ w: 200, h: 200 }, { w: 180, h: 200 }].map(size => ({
    vehicleCategory: 'MOTORCYCLE' as VehicleCategory,
    plateSize: { width: size.w, height: size.h, layout: 'zweizeilig' as PlateLayout },
    plateType: 'REGULAR' as PlateType,
    maxTotalChars: 7,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: `Motorcycle ${size.w}x${size.h}mm - Max 7 chars`,
  })),

  // ============================================================================
  // SPECIAL VEHICLES (125cc, Tractor) - Visual Reality Adjusted
  // ============================================================================

  // Leichtkraft (125cc) - 255 × 130 mm (Height x Width in prompt, usually Width x Height visually)
  // Max: 8 characters
  {
    vehicleCategory: 'LEICHTKRAFT',
    // Prompt says 130 x 255, but reality is 255 Width x 130 Height.
    // We use standard Width x Height notation for proper rendering.
    plateSize: { width: 255, height: 130, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 }, { letters: 1, numbers: 2 }, { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 }, { letters: 2, numbers: 2 }, { letters: 2, numbers: 3 }, { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true, // Visually treated as two-line (stacked)
    seasonalMarkersCount: false,
    description: 'Leichtkraft 255x130mm - Max 8 chars',
  },

  // Traktor - 240/255 × 130 mm (Prompt 130 x 240)
  // Max: 7 characters
  // City code max 2 letters
  {
    vehicleCategory: 'TRAKTOR',
    plateSize: { width: 240, height: 130, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 7,
    maxCityCodeLength: 2, // STRICT: Max 2
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 3 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Traktor 240x130mm - Max 7 chars, City Max 2',
  },
  {
    vehicleCategory: 'TRAKTOR',
    plateSize: { width: 255, height: 130, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 7,
    maxCityCodeLength: 2, // STRICT: Max 2
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 3 }, { letters: 1, numbers: 4 },
      { letters: 2, numbers: 3 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Traktor 255x130mm - Max 7 chars, City Max 2',
  },
]

// Add Special types (Seasonal, E, H) by cloning REGULAR rules
// They inherit all size/char limits
const SPECIAL_TYPES: PlateType[] = [
  'REGULAR_SEASON', 'E', 'E_SEASON', 'H', 'H_SEASON', 'GREEN'
]

const FINAL_RULES: PlateRule[] = [...PLATE_RULES]

PLATE_RULES.forEach(rule => {
  SPECIAL_TYPES.forEach(type => {
    FINAL_RULES.push({
      ...rule,
      plateType: type,
      // Markers do NOT reduce count, so limits stay identical
    })
  })
})

export { FINAL_RULES as PLATE_RULES }

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find matching rule for given parameters
 */
export function findRule(
  vehicleCategory: VehicleCategory,
  plateSize: PlateSize,
  plateType: PlateType
): PlateRule | null {
  // Try exact match
  // 1. Precise Match
  let rule = FINAL_RULES.find(
    r =>
      r.vehicleCategory === vehicleCategory &&
      r.plateSize.width === plateSize.width &&
      r.plateSize.height === plateSize.height &&
      r.plateType === plateType
  )
  if (rule) return rule

  // 2. Fallback: Ignore Type (Use REGULAR)
  rule = FINAL_RULES.find(
    r =>
      r.vehicleCategory === vehicleCategory &&
      r.plateSize.width === plateSize.width &&
      r.plateSize.height === plateSize.height &&
      r.plateType === 'REGULAR'
  )
  if (rule) return rule

  // 3. Fallback: Ignore Size (Use First valid rule for this Vehicle + Type)
  // This prevents "No Rule Found" error if dimensions strictly mismatch (e.g. 1mm diff)
  rule = FINAL_RULES.find(
    r =>
      r.vehicleCategory === vehicleCategory &&
      r.plateType === plateType
  )
  if (rule) return rule

  // 4. Ultimate Fallback: First rule for this Vehicle (Regular)
  return FINAL_RULES.find(r => r.vehicleCategory === vehicleCategory) || null
}

/**
 * Get all rules for a vehicle category
 */
export function getRulesForVehicle(vehicleCategory: VehicleCategory): PlateRule[] {
  return FINAL_RULES.filter(r => r.vehicleCategory === vehicleCategory)
}

/**
 * Get all rules for a plate size
 */
export function getRulesForSize(plateSize: PlateSize): PlateRule[] {
  return FINAL_RULES.filter(
    r =>
      r.plateSize.width === plateSize.width &&
      r.plateSize.height === plateSize.height
  )
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export interface ValidationResult {
  valid: boolean
  error?: string
  reason?: string
}

export interface PlateInput {
  cityCode: string
  letters?: string
  numbers?: string
  saisonStart?: string
  saisonEnd?: string
}

/**
 * Get allowed combinations for a rule based on city code length
 */
function getAllowedCombinationsForCityCode(
  rule: PlateRule,
  cityCodeLength: number
): LetterNumberCombination[] {
  // If rule has city-code-dependent combinations, use those
  if (rule.cityCodeDependentCombinations) {
    if (cityCodeLength === 1 && rule.cityCodeDependentCombinations.cityCodeLength1) {
      return rule.cityCodeDependentCombinations.cityCodeLength1
    }
    if (cityCodeLength === 2 && rule.cityCodeDependentCombinations.cityCodeLength2) {
      return rule.cityCodeDependentCombinations.cityCodeLength2
    }
    if (cityCodeLength === 3 && rule.cityCodeDependentCombinations.cityCodeLength3) {
      return rule.cityCodeDependentCombinations.cityCodeLength3
    }
    return [] // No combinations for this city code length
  }

  // Otherwise use simple allowed combinations
  return rule.allowedCombinations || []
}

/**
 * Validate if a character can be entered in city code section
 */
export function canEnterCityChar(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  const currentCityLength = currentInput.cityCode.length

  // Check max city code length
  if (currentCityLength > rule.maxCityCodeLength) {
    return {
      valid: false,
      error: `City code cannot exceed ${rule.maxCityCodeLength} characters`,
    }
  }

  // Check total length
  const lettersLength = currentInput.letters?.length || 0
  const numbersLength = currentInput.numbers?.length || 0
  const totalLength = currentCityLength + lettersLength + numbersLength

  if (totalLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  return { valid: true }
}

/**
 * Validate if a character can be entered in letters section
 */
export function canEnterLetter(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  if (!currentInput.cityCode || currentInput.cityCode.length === 0) {
    return { valid: false, error: 'City code must be entered first' }
  }

  const currentLettersLength = currentInput.letters?.length || 0
  const currentNumbersLength = currentInput.numbers?.length || 0
  const newLettersLength = currentLettersLength + 1
  const cityLength = currentInput.cityCode.length

  // 1. GLOBAL LIMIT CHECK
  if (cityLength + newLettersLength + currentNumbersLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  // 2. CHECK ALLOWED COMBINATIONS
  const combinations = getAllowedCombinationsForCityCode(rule, cityLength)

  if (combinations.length === 0) {
    return {
      valid: false,
      error: `No combinations allowed for ${cityLength}-letter city code`,
    }
  }

  // Check if ANY combination allows this letter count
  const maxAllowedLetters = Math.max(...combinations.map(c => c.letters))
  if (newLettersLength > maxAllowedLetters) {
    return {
      valid: false,
      error: `Maximum ${maxAllowedLetters} letters allowed`,
    }
  }

  // Check if specific combination exists with current numbers
  if (currentNumbersLength > 0) {
    const isValid = combinations.some(
      c => c.letters === newLettersLength && c.numbers === currentNumbersLength
    )
    if (!isValid) {
      return {
        valid: false,
        error: `Invalid combination`,
      }
    }
  }

  return { valid: true }
}

/**
 * Validate if a character can be entered in numbers section
 */
export function canEnterNumber(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  if (!currentInput.cityCode || currentInput.cityCode.length === 0) {
    return { valid: false, error: 'City code must be entered first' }
  }

  const currentLettersLength = currentInput.letters?.length || 0
  const currentNumbersLength = currentInput.numbers?.length || 0
  const newNumbersLength = currentNumbersLength + 1
  const cityLength = currentInput.cityCode.length

  // 1. GLOBAL LIMIT CHECK
  if (cityLength + currentLettersLength + newNumbersLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  // 2. CHECK ALLOWED COMBINATIONS
  const combinations = getAllowedCombinationsForCityCode(rule, cityLength)

  if (combinations.length === 0) {
    return {
      valid: false,
      error: `No combinations allowed for ${cityLength}-letter city code`,
    }
  }

  // Check if ANY combination allows this number count
  const maxAllowedNumbers = Math.max(...combinations.map(c => c.numbers))
  if (newNumbersLength > maxAllowedNumbers) {
    return {
      valid: false,
      error: `Maximum ${maxAllowedNumbers} numbers allowed`,
    }
  }

  // Check if specific combination exists with current letters
  if (currentLettersLength > 0) {
    const isValid = combinations.some(
      c => c.letters === currentLettersLength && c.numbers === newNumbersLength
    )
    if (!isValid) {
      return {
        valid: false,
        error: `Invalid combination`,
      }
    }
  }

  return { valid: true }
}

/**
 * Validate complete plate input
 */
export function validatePlateInput(
  rule: PlateRule | null,
  input: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  if (!input.cityCode || input.cityCode.length < rule.minCityCodeLength) {
    return { valid: false, error: `City code too short` }
  }

  if (input.cityCode.length > rule.maxCityCodeLength) {
    return { valid: false, error: `City code too long` }
  }

  // allow 0 letters if rule permits (checked below in combinations)
  const lettersLength = input.letters?.length || 0

  const numbersLength = input.numbers?.length || 0
  if (numbersLength === 0) {
    return { valid: false, error: 'Numbers missing' }
  }

  const combinations = getAllowedCombinationsForCityCode(rule, input.cityCode.length)
  const isValid = combinations.some(
    c => c.letters === lettersLength && c.numbers === numbersLength
  )

  if (!isValid) {
    return { valid: false, error: 'Invalid combination' }
  }

  const totalLength = input.cityCode.length + lettersLength + numbersLength
  if (totalLength > rule.maxTotalChars) {
    return { valid: false, error: `Too many characters (Max ${rule.maxTotalChars})` }
  }

  return { valid: true }
}

/**
 * Get allowed letter counts for current input state
 */
export function getAllowedLetterCounts(
  rule: PlateRule | null,
  cityCodeLength: number,
  numbersLength: number
): number[] {
  if (!rule) return []
  const combinations = getAllowedCombinationsForCityCode(rule, cityCodeLength)
  const validCombinations = combinations.filter(c =>
    cityCodeLength + c.letters + numbersLength <= rule.maxTotalChars
  )
  return [...new Set(validCombinations.map(c => c.letters))].sort()
}

/**
 * Get allowed number counts for current input state
 */
export function getAllowedNumberCounts(
  rule: PlateRule | null,
  cityCodeLength: number,
  lettersLength: number
): number[] {
  if (!rule) return []
  const combinations = getAllowedCombinationsForCityCode(rule, cityCodeLength)
  const validCombinations = combinations.filter(c =>
    cityCodeLength + lettersLength + c.numbers <= rule.maxTotalChars
  )
  return [...new Set(validCombinations.map(c => c.numbers))].sort()
}

// ============================================================================
// PARSING & SANITIZATION
// ============================================================================

export interface ParsedPlate {
  raw: PlateInput
  formatted: PlateInput
  validation: ValidationResult
}

/**
 * Parse raw user input strictly without mutation or placeholders.
 * Returns both raw input (for display) and formatted input (for API/Validation).
 */
export function parseRawInput(
  raw: PlateInput,
  rule: PlateRule | null
): ParsedPlate {
  // RAW: Keep exactly what user typed for the Input fields (but maybe default to empty string if undefined)
  const safeRaw: PlateInput = {
    cityCode: raw.cityCode || '',
    letters: raw.letters || '',
    numbers: raw.numbers || ''
  }

  // FORMATTED: This is for the API/Search/Parent state.
  // We strip invalid characters here to ensure API safety.
  // We NEVER add placeholders like "??" for missing values.
  const cleanCity = (safeRaw.cityCode || '').toUpperCase().replace(/[^A-ZÄÖÜ?]/g, '')
  const cleanLetters = (safeRaw.letters || '').toUpperCase().replace(/[^A-ZÄÖÜ?]/g, '')
  const cleanNumbers = (safeRaw.numbers || '').replace(/[^0-9?]/g, '')

  const formatted: PlateInput = {
    cityCode: cleanCity,
    letters: cleanLetters,
    numbers: cleanNumbers
  }

  // VALIDATION: Check the *Formatted* version against rules
  // We validate separately so UI can show errors without blocking typing
  const validation = validatePlateInput(rule, formatted)

  return {
    raw: safeRaw,
    formatted,
    validation
  }
}
